import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import "./Services.css";
import { Images } from "../../helpers/Images";
import { Gallery } from "../../helpers/gallery";
import { useForm } from "@formspree/react";
import { toast } from "react-toastify";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendIcon from "@mui/icons-material/Send";

const SERVICES = [
  {
    title: "STEAM Based Curriculum",
    tag: "For Students",
    description:
      "Comprehensive learning programs designed to develop technical and soft skills in robotics, programming, and innovation.",
    image: Images.rec,
    icon: SchoolOutlinedIcon,
    features: [
      "Hands-on robotics projects",
      "Programming fundamentals",
      "Problem-solving workshops",
      "Team collaboration activities",
    ],
  },
  {
    title: "Organization Partnerships",
    tag: "For Institutions",
    description:
      "Collaborative programs for educational institutions and organizations to implement our innovative learning approach.",
    image: Gallery.gallery39,
    icon: BusinessOutlinedIcon,
    features: [
      "Customized curriculum integration",
      "Professional development support",
      "Resource sharing and networking",
      "Competition participation",
    ],
  },
  {
    title: "Trainer Development",
    tag: "For Educators",
    description:
      "Comprehensive training programs for educators and trainers to become certified WeDo Challenge instructors.",
    image: Gallery.gallery48,
    icon: MenuBookOutlinedIcon,
    features: [
      "Certification programs",
      "Teaching methodologies",
      "Technical skill development",
      "Ongoing support and resources",
    ],
  },
];

const HIGHLIGHTS = [
  {
    title: "Hands-on Learning",
    description: "Project-based STEAM education that builds real-world skills.",
  },
  {
    title: "Scalable Programs",
    description: "Solutions tailored for classrooms, clubs, and organizations.",
  },
  {
    title: "Ongoing Support",
    description: "Resources and guidance from our team every step of the way.",
  },
];

const ServicesTemplate = () => {
  const [state, handleSubmit] = useForm("xzzrvldl");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    if (state.succeeded) {
      setFormData({
        name: "",
        phone: "",
        email: "",
        message: "",
      });
      toast.success(
        "Thank you for filling the form! We will get back to you as soon as possible.",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          style: { backgroundColor: "#c11a83" },
        },
      );
    }
  }, [state.succeeded]);

  const scrollToForm = () => {
    document
      .getElementById("contact-form")
      .scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box className="servicesPage">
      <Box
        className="servicesHero"
        sx={{
          backgroundImage: `radial-gradient(1100px 620px at 15% 15%, rgba(35, 174, 244, 0.38), transparent 58%),
            radial-gradient(900px 520px at 88% 25%, rgba(193, 26, 131, 0.32), transparent 55%),
            linear-gradient(180deg, rgba(10, 14, 24, 0.78) 0%, rgba(10, 14, 24, 0.62) 100%),
            url(${Images.gold})`,
        }}
      >
        <Box
          className="servicesHeroInner"
          sx={{
            padding: {
              xs: "56px 20px 40px",
              sm: "72px 60px 48px",
              lg: "88px 120px 56px",
            },
          }}
        >
          <Typography className="servicesHeroEyebrow">We Do Challenge</Typography>
          <Typography className="servicesHeroTitle">Our Services</Typography>
          <Box className="servicesHeroAccent" />
          <Typography className="servicesHeroSubtitle">
            Empowering the next generation through innovative education and
            training programs in robotics, programming, and creative
            problem-solving.
          </Typography>
          <Button
            variant="contained"
            onClick={scrollToForm}
            endIcon={<ArrowForwardIcon />}
            className="servicesHeroCta"
          >
            Get Started
          </Button>

          <Box className="servicesHeroHighlights">
            {HIGHLIGHTS.map((item) => (
              <Box key={item.title} className="servicesHeroHighlight">
                <Typography className="servicesHeroHighlightTitle">
                  {item.title}
                </Typography>
                <Typography className="servicesHeroHighlightText">
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          padding: { xs: "40px 20px", sm: "50px 60px", lg: "50px 120px" },
          maxWidth: "1500px",
          margin: "0 auto",
        }}
      >
        <Grid container spacing={3}>
          {SERVICES.map((service, index) => {
            const Icon = service.icon;
            return (
              <Grid item xs={12} md={4} key={service.title}>
                <Box className="serviceCard">
                  <Box className="serviceCardImageWrap">
                    <Box
                      component="img"
                      src={service.image}
                      alt={service.title}
                      className="serviceCardImage"
                    />
                    <Box className="serviceCardImageOverlay" />
                    <Box className="serviceCardBadge">
                      <Icon sx={{ fontSize: 18 }} />
                      <span>{service.tag}</span>
                    </Box>
                  </Box>

                  <Box className="serviceCardBody">
                    <Typography className="serviceCardIndex">
                      0{index + 1}
                    </Typography>
                    <Typography className="serviceCardTitle">
                      {service.title}
                    </Typography>
                    <Typography className="serviceCardDescription">
                      {service.description}
                    </Typography>

                    <Box className="serviceFeatures">
                      {service.features.map((feature) => (
                        <Box key={feature} className="serviceFeature">
                          <CheckCircleOutlineIcon className="serviceFeatureIcon" />
                          <Typography>{feature}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      variant="contained"
                      onClick={scrollToForm}
                      endIcon={<ArrowForwardIcon />}
                      className="serviceCardButton"
                    >
                      Get Started
                    </Button>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Box id="contact-form" className="contactSection">
        <Box
          sx={{
            maxWidth: "1500px",
            margin: "0 auto",
            padding: { xs: "50px 20px", sm: "60px 60px", lg: "80px 120px" },
          }}
        >
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={5}>
              <Box className="contactIntro">
                <Typography className="contactEyebrow">
                  Get in touch
                </Typography>
                <Typography className="contactTitle">
                  Ready to get started?
                </Typography>
                <Typography className="contactDescription">
                  We&apos;re excited to help you take the next step in your
                  educational journey. Fill out the form and one of our team
                  members will contact you within 24 hours.
                </Typography>
                <Box className="contactPoints">
                  <Box className="contactPoint">
                    <CheckCircleOutlineIcon />
                    <span>Student programs and STEAM curriculum</span>
                  </Box>
                  <Box className="contactPoint">
                    <CheckCircleOutlineIcon />
                    <span>Organization and school partnerships</span>
                  </Box>
                  <Box className="contactPoint">
                    <CheckCircleOutlineIcon />
                    <span>Trainer certification and development</span>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box
                component="form"
                onSubmit={handleSubmit}
                className="contactForm"
              >
                <Typography className="contactFormTitle">
                  Send us a message
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      variant="outlined"
                      className="contactField"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      variant="outlined"
                      className="contactField"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      variant="outlined"
                      className="contactField"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      variant="outlined"
                      className="contactField"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      endIcon={<SendIcon />}
                      className="contactSubmitButton"
                      disabled={state.submitting}
                    >
                      {state.submitting ? "Sending..." : "Send Message"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default ServicesTemplate;
